import { z } from 'zod';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';

/**
 * Abonnements facturés hors Stripe (lot J, demande de Maxime du 13/10/2026) : certains clients paient leur
 * abonnement par une facture faite à la main. Ils sont saisis dans le cockpit (table `manual_subscriptions`,
 * administrateur) et comptent dans le revenu mensuel récurrent comme un abonnement Stripe actif.
 * Le cockpit ne crée toujours rien dans Stripe (CDC §3.2 ter) ; les factures d'abonnement d'Axonaut ne sont
 * jamais ajoutées automatiquement (le back recopie déjà les factures Stripe dans Axonaut : double compte).
 *
 * Routes : `GET/POST /api/admin/manual-subscriptions`, `PATCH/DELETE /api/admin/manual-subscriptions/:id`
 * (administrateur, journal d'audit). Les membres lisent la liste dans `GET /api/metrics/subscriptions`
 * (`manual`).
 */

/** Libellé par défaut d'un abonnement hors Stripe. */
export const MANUAL_SUBSCRIPTION_DEFAULT_LABEL = 'Hors Stripe';

/**
 * Lot N (demandes de Maxime du 13/10/2026 : « certains clients sont facturés tous les 2 ou 3 ans », puis « un
 * nombre de mois libre, ex. 17 mois ») : la période de facturation est un nombre de mois, de 1 à 120
 * (`period_months`). 1 = mensuel, 12 = annuel, 24, 36, 17… Remplace `interval` (`month|year`) du lot J :
 * la migration `202610131800` reprend `month` → 1 et `year` → 12.
 */
export const ManualPeriodMonths = z
  .int('Nombre de mois entier attendu')
  .min(1, 'Au moins 1 mois')
  .max(120, 'Au plus 120 mois (10 ans)');
export type ManualPeriodMonths = z.infer<typeof ManualPeriodMonths>;

/** Raccourcis du formulaire « Facturé tous les … mois » (Chiffres) ; tout autre nombre de mois se saisit. */
export const MANUAL_PERIOD_PRESETS: readonly { months: number; label: string }[] = [
  { months: 1, label: '1 mois' },
  { months: 12, label: '12 mois (1 an)' },
  { months: 24, label: '24 mois' },
  { months: 36, label: '36 mois' },
];

/**
 * Lot N : délai du rappel « Facturer l'abonnement » propre à un abonnement, en jours avant la date de
 * facturation (0 à 365 ; l'écran le saisit en semaines ou en jours). null = délai par défaut des Réglages
 * (`settings.rules.manualSubscriptionInvoice.daysBefore`, 7 jours).
 */
export const ManualReminderLeadDays = z
  .int('Nombre de jours entier attendu')
  .min(0, 'Délai positif attendu')
  .max(365, 'Au plus 365 jours');

/**
 * `active` : commencé (`startDate` ≤ aujourd'hui) et pas terminé (`endDate` vide ou ≥ aujourd'hui) : il compte ;
 * `upcoming` : commence plus tard ; `ended` : terminé (`endDate` < aujourd'hui). Seuls les `active` comptent.
 */
export const ManualSubscriptionStatus = z.enum(['active', 'upcoming', 'ended']);
export type ManualSubscriptionStatus = z.infer<typeof ManualSubscriptionStatus>;

export const ManualSubscription = z.object({
  id: Uuid,
  clientId: Uuid,
  clientName: z.string(),
  label: z.string(),
  /** Montant HT par période (`periodMonths`), en centimes. */
  amountCents: Cents,
  /** Période de facturation en mois (1 = mensuel, 12 = annuel, 24, 36…). */
  periodMonths: ManualPeriodMonths,
  /** Montant ramené au mois (montant / nombre de mois, arrondi) : ce qui compte dans le revenu mensuel récurrent. */
  monthlyAmountCents: Cents,
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  /**
   * Lot N : prochaine date de facturation (la première de la suite début, début + période… qui tombe
   * aujourd'hui ou plus tard, sans dépasser `endDate`) ; null si l'abonnement n'a plus de facture à venir.
   */
  nextInvoiceDate: IsoDate.nullable(),
  /** Lot N : délai du rappel propre à l'abonnement (jours), null = délai par défaut des Réglages. */
  reminderLeadDays: ManualReminderLeadDays.nullable(),
  /** Délai appliqué : `reminderLeadDays`, sinon le délai par défaut. */
  effectiveReminderLeadDays: ManualReminderLeadDays,
  /** Date où « Facturer l'abonnement » arrive dans « À faire » pour `nextInvoiceDate` (null sans prochaine facture). */
  nextReminderDate: IsoDate.nullable(),
  note: z.string().nullable(),
  /**
   * Lot V : formule de l'abonnement (une des formules connues, cf. `ManualSubscriptionsAdmin.planNames`) ; null =
   * « Autre ». La carte « Abonnements par formule » de Chiffres le range sous cette formule.
   */
  planName: z.string().nullable(),
  status: ManualSubscriptionStatus,
  createdBy: Uuid.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime.nullable(),
});
export type ManualSubscription = z.infer<typeof ManualSubscription>;

const Label = z.string().trim().min(1, 'Libellé requis').max(100);
const AmountCents = z.int().min(1, 'Montant attendu supérieur à 0').max(100_000_000, 'Montant trop élevé');
const Note = z.string().trim().max(1000).nullable();
/** Lot V : formule (null = « Autre »). */
const PlanName = z.string().trim().min(1).max(100).nullable();

/** `POST /api/admin/manual-subscriptions`. `endDate` ≥ `startDate` (sinon 400 `VALIDATION_ERROR`). */
export const CreateManualSubscriptionBody = z
  .object({
    clientId: Uuid,
    /** Par défaut « Hors Stripe ». */
    label: Label.optional(),
    amountCents: AmountCents,
    periodMonths: ManualPeriodMonths,
    startDate: IsoDate,
    endDate: IsoDate.nullable().optional(),
    /** Absent ou null : délai par défaut des Réglages. */
    reminderLeadDays: ManualReminderLeadDays.nullable().optional(),
    note: Note.optional(),
    /** Lot V : formule ; absent ou null = « Autre ». */
    planName: PlanName.optional(),
  })
  .strict();
export type CreateManualSubscriptionBody = z.infer<typeof CreateManualSubscriptionBody>;

/**
 * `PATCH /api/admin/manual-subscriptions/:id` : champs à modifier (au moins un). Terminer un abonnement =
 * `{ endDate }` ; `endDate: null` le relance.
 */
export const UpdateManualSubscriptionBody = z
  .object({
    clientId: Uuid.optional(),
    label: Label.optional(),
    amountCents: AmountCents.optional(),
    periodMonths: ManualPeriodMonths.optional(),
    startDate: IsoDate.optional(),
    endDate: IsoDate.nullable().optional(),
    /** null : revenir au délai par défaut. */
    reminderLeadDays: ManualReminderLeadDays.nullable().optional(),
    note: Note.optional(),
    /** Lot V : null = « Autre ». */
    planName: PlanName.optional(),
  })
  .strict();
export type UpdateManualSubscriptionBody = z.infer<typeof UpdateManualSubscriptionBody>;

/**
 * Suggestion (lecture seule) : client avec au moins 2 factures Axonaut de type `subscription` sur les
 * 120 derniers jours, sans abonnement Stripe actif ni abonnement hors Stripe en cours ou à venir.
 * « Ajouter » préremplit le client et le montant HT de la dernière facture (mensuel).
 */
export const ManualSubscriptionSuggestion = z.object({
  clientId: Uuid,
  clientName: z.string(),
  invoiceCount: z.int().min(0),
  lastInvoiceDate: IsoDate,
  lastInvoiceNumber: z.string().nullable(),
  lastInvoiceAmountCents: Cents,
});
export type ManualSubscriptionSuggestion = z.infer<typeof ManualSubscriptionSuggestion>;

/** `GET /api/admin/manual-subscriptions` : la liste, les clients (sélecteur) et les suggestions. */
export const ManualSubscriptionsAdmin = z.object({
  items: z.array(ManualSubscription),
  /** Lot N : avec l'identifiant Axonaut, cherchable dans le sélecteur (« Axonaut 13340495 »). */
  clients: z.array(z.object({ id: Uuid, name: z.string(), axonautCompanyId: z.string().nullable() })),
  suggestions: z.array(ManualSubscriptionSuggestion),
  /** Lot N : délai par défaut du rappel de facturation (Réglages), en jours, affiché « par défaut (1 semaine) ». */
  defaultReminderLeadDays: ManualReminderLeadDays,
  /**
   * Lot V : formules connues pour le choix « Formule » (formules des abonnements Stripe lus dans le back, puis
   * celles déjà choisies hors Stripe), dans l'ordre alphabétique, sans doublon. Le back ne donne que la formule
   * de base d'un abonnement (pas ses options ni le catalogue complet, CDC §3.9).
   */
  planNames: z.array(z.string()),
});
export type ManualSubscriptionsAdmin = z.infer<typeof ManualSubscriptionsAdmin>;
